import * as turf from "@turf/turf";

const STATE_KEY = "hydrotools:depth-extraction:v1";
const SLIP_API_BASE = "/api/slip-inventory/v1/slip-inventory";
const DEPTH_API = "/api/point-cloud/depth-sample";

export const depthExtractionTool = {
  id: "depth-extraction",
  name: "Depth Extraction",
  init(app) { this._rt = this._rt || createRuntime(app); },
  destroy() { this._rt?.destroy(); this._rt = null; },
  renderPanel(app, containerEl) { if (!this._rt) this._rt = createRuntime(app); this._rt.mount(containerEl); },
  serialize() { return this._rt?.snapshot() || null; },
  restore(app, data) { if (!this._rt) this._rt = createRuntime(app); this._rt.restore(data); },
};

function createRuntime(_app) {
  let containerEl = null;
  let slips = [];
  let results = [];
  let busy = false;
  let status = "";
  let state = readState();

  function esc(v) {
    return String(v ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll("\"", "&quot;");
  }

  function readState() {
    try {
      const raw = localStorage.getItem(STATE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return {
        sourcePath: String(parsed?.sourcePath || ""),
        marinaId: String(parsed?.marinaId || ""),
        radiusFeet: Math.max(0.5, Math.min(500, Number(parsed?.radiusFeet) || 6)),
        maxNearestFeet: Math.max(1, Math.min(2000, Number(parsed?.maxNearestFeet) || 30)),
        method: ["nearest", "mean", "median", "min", "max"].includes(String(parsed?.method || "").toLowerCase())
          ? String(parsed.method).toLowerCase()
          : "mean",
      };
    } catch {
      return { sourcePath: "", marinaId: "", radiusFeet: 6, maxNearestFeet: 30, method: "mean" };
    }
  }

  function persist() {
    try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
  }

  function parseWktGeometry(wkt) {
    const text = String(wkt || "").trim();
    if (!text) return null;

    const pointMatch = /^POINT\s*\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)$/i.exec(text);
    if (pointMatch) {
      const lon = Number(pointMatch[1]); const lat = Number(pointMatch[2]);
      if (Number.isFinite(lon) && Number.isFinite(lat)) return turf.point([lon, lat]);
      return null;
    }

    const polygonMatch = /^POLYGON\s*\(\(\s*(.+?)\s*\)\)$/i.exec(text);
    if (!polygonMatch) return null;
    const ring = String(polygonMatch[1] || "")
      .split(",")
      .map((chunk) => String(chunk || "").trim().split(/\s+/))
      .map((parts) => {
        if (parts.length < 2) return null;
        const lon = Number(parts[0]); const lat = Number(parts[1]);
        return Number.isFinite(lon) && Number.isFinite(lat) ? [lon, lat] : null;
      })
      .filter(Boolean);
    if (ring.length < 3) return null;
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (!last || first[0] !== last[0] || first[1] !== last[1]) ring.push([first[0], first[1]]);
    return turf.polygon([ring]);
  }

  function featureCenter(feature) {
    try {
      return turf.pointOnFeature(feature);
    } catch {
      return turf.centroid(feature);
    }
  }

  function toCentroidPoint(record, idx) {
    const geom = parseWktGeometry(record.geometry_wkt);
    if (!geom) return null;
    const center = featureCenter(geom);
    const lon = Number(center?.geometry?.coordinates?.[0]);
    const lat = Number(center?.geometry?.coordinates?.[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
    return {
      id: String(record.measurement_id || record.slip_id || `slip-${idx + 1}`),
      slip_id: String(record.slip_id || ""),
      marina_id: String(record.marina_id || ""),
      lon,
      lat,
    };
  }

  async function loadSlips() {
    busy = true;
    status = "Loading slip geometry...";
    render();
    try {
      const params = new URLSearchParams({ limit: "5000", offset: "0" });
      if (state.marinaId) params.set("marina_id", state.marinaId);
      const res = await fetch(`${SLIP_API_BASE}?${params.toString()}`);
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || !payload?.ok) throw new Error(String(payload?.error || "slip_load_failed"));
      const items = Array.isArray(payload.items) ? payload.items : [];
      slips = items;
      status = `Loaded ${slips.length} slip records.`;
    } catch (error) {
      status = String(error?.message || error);
      slips = [];
    } finally {
      busy = false;
      render();
    }
  }

  async function runExtraction() {
    const sourcePath = String(state.sourcePath || "").trim();
    if (!sourcePath) { status = "Set LAS source S3 key first."; render(); return; }
    if (!slips.length) { status = "Load slips first."; render(); return; }

    const points = slips.map((record, idx) => toCentroidPoint(record, idx)).filter(Boolean);
    if (!points.length) { status = "No valid slip geometries found."; render(); return; }

    busy = true;
    status = `Sampling ${points.length} slip centroid(s)...`;
    render();
    try {
      const res = await fetch(DEPTH_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sourcePath,
          points: points.map((p) => ({ id: p.id, lon: p.lon, lat: p.lat })),
          radiusFeet: Number(state.radiusFeet),
          maxNearestFeet: Number(state.maxNearestFeet),
          method: state.method,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || !payload?.ok) throw new Error(String(payload?.error || "depth_sample_failed"));

      const byId = new Map((payload.results || []).map((r) => [String(r.id || ""), r]));
      results = points.map((p) => ({
        ...p,
        ...(byId.get(String(p.id || "")) || { ok: false, error: "missing_result" }),
      }));
      const okCount = results.filter((x) => x.ok).length;
      status = `Depth extraction complete: ${okCount}/${results.length} slips sampled.`;
    } catch (error) {
      results = [];
      status = String(error?.message || error);
    } finally {
      busy = false;
      render();
    }
  }

  function exportCsv() {
    if (!results.length) return;
    const lines = [
      "id,slip_id,marina_id,lon,lat,depth,method,radiusFeet,pointsUsed,nearestDistanceFeet,qualityFlag,ok,error",
    ];
    for (const r of results) {
      lines.push([
        csv(r.id), csv(r.slip_id), csv(r.marina_id), csv(r.lon), csv(r.lat), csv(r.depth),
        csv(r.method), csv(r.radiusFeet), csv(r.pointsUsed), csv(r.nearestDistanceFeet),
        csv(r.qualityFlag), csv(r.ok), csv(r.error),
      ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `depth-extraction-${new Date().toISOString().replaceAll(":", "").slice(0, 15)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function csv(value) {
    const s = String(value ?? "");
    if (!/[",\n]/.test(s)) return s;
    return `"${s.replaceAll("\"", "\"\"")}"`;
  }

  function readInputs() {
    if (!containerEl) return;
    state.sourcePath = String(containerEl.querySelector("#de-las-source")?.value || "").trim();
    state.marinaId = String(containerEl.querySelector("#de-marina")?.value || "").trim();
    state.radiusFeet = Math.max(0.5, Math.min(500, Number(containerEl.querySelector("#de-radius")?.value) || 6));
    state.maxNearestFeet = Math.max(1, Math.min(2000, Number(containerEl.querySelector("#de-max-nearest")?.value) || 30));
    state.method = String(containerEl.querySelector("#de-method")?.value || "mean").toLowerCase();
    persist();
  }

  function onClick(event) {
    const target = event.target?.closest?.("button");
    if (!target) return;
    readInputs();
    if (target.id === "de-load-slips") loadSlips();
    if (target.id === "de-convert-xyz") convertXyz();
    if (target.id === "de-run") runExtraction();
    if (target.id === "de-export-csv") exportCsv();
  }

  async function convertXyz() {
    const fileInput = containerEl?.querySelector?.("#de-xyz-file");
    const file = fileInput?.files?.[0];
    if (!file) {
      status = "Select an XYZ/CSV/PTS file first.";
      render();
      return;
    }
    busy = true;
    status = `Converting ${file.name} to LAZ...`;
    render();
    try {
      const res = await fetch(`/api/point-cloud/convert-xyz?name=${encodeURIComponent(file.name)}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/octet-stream",
          "x-file-name": String(file.name || "source.xyz"),
        },
        body: file,
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || !payload?.ok || !payload?.sourcePath) {
        throw new Error(String(payload?.error || "xyz_conversion_failed"));
      }
      state.sourcePath = String(payload.sourcePath || "");
      persist();
      status = `Converted and uploaded. Using LAS source: ${state.sourcePath}`;
    } catch (error) {
      status = String(error?.message || error);
    } finally {
      busy = false;
      render();
    }
  }

  function render() {
    if (!containerEl) return;
    containerEl.innerHTML = `
      <div class="toolset-group" open>
        <div class="toolset-content">
          <div class="small" style="font-weight:600;">Depth Extraction (Slip Centroids)</div>
          <div class="small" style="margin-top:4px; opacity:.75;">Extract bathy depth from an S3 LAS/LAZ at each slip centroid (point-on-surface).</div>
          <div class="row lm-add-row" style="margin-top:8px; display:block;">
            <label class="small" style="display:block; margin-bottom:4px;">LAS Source (S3 key under point-clouds/)</label>
            <input id="de-las-source" type="text" value="${esc(state.sourcePath)}" placeholder="point-clouds/your-bathy-file.laz" style="width:100%;" />
          </div>
          <div class="row lm-add-row" style="margin-top:6px; display:block;">
            <label class="small" style="display:block; margin-bottom:4px;">Marina Filter (optional marina_id)</label>
            <input id="de-marina" type="text" value="${esc(state.marinaId)}" placeholder="LLM" style="width:100%;" />
          </div>
          <div class="row lm-add-row" style="margin-top:6px;">
            <div style="flex:1;">
              <label class="small" style="display:block; margin-bottom:4px;">Radius (ft)</label>
              <input id="de-radius" type="number" min="0.5" max="500" step="0.5" value="${esc(state.radiusFeet)}" style="width:100%;" />
            </div>
            <div style="flex:1;">
              <label class="small" style="display:block; margin-bottom:4px;">Nearest Fallback Max (ft)</label>
              <input id="de-max-nearest" type="number" min="1" max="2000" step="1" value="${esc(state.maxNearestFeet)}" style="width:100%;" />
            </div>
          </div>
          <div class="row lm-add-row" style="margin-top:6px; display:block;">
            <label class="small" style="display:block; margin-bottom:4px;">Method</label>
            <select id="de-method" style="width:100%;">
              <option value="mean"${state.method === "mean" ? " selected" : ""}>Mean</option>
              <option value="median"${state.method === "median" ? " selected" : ""}>Median</option>
              <option value="nearest"${state.method === "nearest" ? " selected" : ""}>Nearest</option>
              <option value="min"${state.method === "min" ? " selected" : ""}>Min</option>
              <option value="max"${state.method === "max" ? " selected" : ""}>Max</option>
            </select>
          </div>
          <div class="row lm-add-row" style="margin-top:8px;">
            <button id="de-load-slips" class="btn"${busy ? " disabled" : ""}>1) Load Slips</button>
            <button id="de-run" class="btn primary"${busy ? " disabled" : ""}>3) Run Depth Extraction</button>
            <button id="de-export-csv" class="btn"${busy || !results.length ? " disabled" : ""}>4) Export CSV</button>
          </div>
          <div class="row lm-add-row" style="margin-top:6px;">
            <input id="de-xyz-file" type="file" accept=".xyz,.txt,.csv,.pts" />
            <button id="de-convert-xyz" class="btn"${busy ? " disabled" : ""}>2) Upload XYZ and Convert</button>
          </div>
          <div class="small" style="margin-top:8px; color:${busy ? "#7dd3fc" : "#cbd5e1"};">${esc(status || "Idle.")}</div>
          <div class="small" style="margin-top:4px; opacity:.75;">Loaded slips: ${esc(slips.length)} | Results: ${esc(results.length)}</div>
          <div style="margin-top:8px; max-height:220px; overflow:auto; border:1px solid rgba(148,163,184,.22); border-radius:8px; padding:6px;">
            ${results.length ? `
              <table style="width:100%; border-collapse:collapse; font-size:11px;">
                <thead><tr><th style="text-align:left;">Slip</th><th style="text-align:right;">Depth</th><th style="text-align:right;">Pts</th><th style="text-align:left;">Flag</th></tr></thead>
                <tbody>
                  ${results.slice(0, 300).map((r) => `
                    <tr>
                      <td>${esc(r.slip_id || r.id)}</td>
                      <td style="text-align:right;">${esc(r.ok ? r.depth : "n/a")}</td>
                      <td style="text-align:right;">${esc(r.pointsUsed || 0)}</td>
                      <td>${esc(r.qualityFlag || r.error || "n/a")}</td>
                    </tr>
                  `).join("")}
                </tbody>
              </table>
            ` : `<div class="small" style="opacity:.75;">No extraction results yet.</div>`}
          </div>
        </div>
      </div>
    `;
  }

  function mount(el) {
    containerEl = el;
    containerEl.addEventListener("click", onClick);
    render();
  }

  function destroy() {
    if (!containerEl) return;
    containerEl.removeEventListener("click", onClick);
    containerEl = null;
  }

  function snapshot() {
    return { version: 1, state: { ...state } };
  }

  function restore(data) {
    const next = data?.state;
    if (!next || typeof next !== "object") return;
    state = {
      ...state,
      ...next,
      sourcePath: String(next.sourcePath || state.sourcePath || ""),
      marinaId: String(next.marinaId || state.marinaId || ""),
      radiusFeet: Math.max(0.5, Math.min(500, Number(next.radiusFeet) || state.radiusFeet || 6)),
      maxNearestFeet: Math.max(1, Math.min(2000, Number(next.maxNearestFeet) || state.maxNearestFeet || 30)),
      method: ["nearest", "mean", "median", "min", "max"].includes(String(next.method || "").toLowerCase())
        ? String(next.method).toLowerCase()
        : state.method,
    };
    persist();
    render();
  }

  return { mount, destroy, snapshot, restore };
}
